import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { StyledCircularChip } from '@openx/components-core/lib/Chip/StyledChip.ts';
import { ValuePlaceholder } from '@openx/components-core/lib/Value/ValuePlaceholder/ValuePlaceholder.tsx';
import { useMemo } from 'react';

const ROWS_IN_TOOLTIP = 10;

type ItemsPreviewCellProps = {
  items: string[];
  label?: string;
  numberOfItemsToDisplay?: number;
  onClick?: () => void;
};

export const ItemsPreviewCell = ({ items, label, numberOfItemsToDisplay = 1, onClick }: ItemsPreviewCellProps) => {
  const { itemsToDisplay, itemsInTooltipCount, tooltip } = useMemo(() => {
    const itemsToDisplay = items.slice(0, numberOfItemsToDisplay);
    const itemsInTooltipCount = items.length - numberOfItemsToDisplay;

    const finalRowText = items.length > ROWS_IN_TOOLTIP ? `...\nand ${items.length - ROWS_IN_TOOLTIP} more.` : '';
    const tooltip = `${items.slice(numberOfItemsToDisplay, ROWS_IN_TOOLTIP).join(',\n')}${finalRowText}`;

    return { itemsToDisplay, itemsInTooltipCount, tooltip };
  }, [items, numberOfItemsToDisplay]);

  if (!itemsToDisplay.length) {
    return <ValuePlaceholder />;
  }

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: 1,
      }}
    >
      {label && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            wordBreak: 'keep-all',
          }}
        >
          {label}:
        </Typography>
      )}

      {itemsToDisplay.map((item, index) => (
        <Tooltip title={item} key={index}>
          <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'keep-all' }}>
            {item}
            {index !== itemsToDisplay.length - 1 && ','}
          </Typography>
        </Tooltip>
      ))}

      {itemsInTooltipCount > 0 && (
        <Tooltip title={<pre>{tooltip}</pre>}>
          <StyledCircularChip
            clickable={!!onClick}
            data-test="multi-value-chip"
            label={itemsInTooltipCount}
            onClick={onClick}
          />
        </Tooltip>
      )}
    </Stack>
  );
};
